import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Building2, Search, ChevronRight } from "lucide-react";

const empty = { name: "", industry: "", vat: "", website: "", phone: "", email: "", address: "", city: "", notes: "" };

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/companies").then((r) => setCompanies(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Il nome azienda è obbligatorio"); return; }
    try {
      await api.post("/companies", form);
      toast.success("Azienda creata");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = companies.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) || (c.city || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-10">
      <PageHeader subtitle="CRM" title="Contatti / Aziende"
        action={
          <Button data-testid="new-company-btn" onClick={() => setOpen(true)}
            className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm transition-colors duration-200">
            <Plus className="w-4 h-4 mr-2" /> Nuovo Contatto
          </Button>
        } />

      <div className="relative mb-4 max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="company-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca azienda o città..." className="pl-9 rounded-sm bg-white" />
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm py-20 text-center">
          <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-4" />
          <div className="font-display font-bold text-lg text-slate-900">Nessuna azienda</div>
          <p className="text-sm text-slate-500 mt-1">Aggiungi il tuo primo cliente per iniziare.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left font-mono-label text-[10px] text-slate-500 px-6 py-3">Azienda</th>
                <th className="text-left font-mono-label text-[10px] text-slate-500 px-6 py-3">Settore</th>
                <th className="text-left font-mono-label text-[10px] text-slate-500 px-6 py-3">Città</th>
                <th className="text-left font-mono-label text-[10px] text-slate-500 px-6 py-3">Email</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={c.id} data-testid={`company-row-${c.id}`}
                  onClick={() => navigate(`/aziende/${c.id}`)}
                  className={`cursor-pointer hover:bg-slate-50 transition-colors duration-150 ${i % 2 ? "bg-slate-50/40" : ""}`}>
                  <td className="px-6 py-4 font-medium text-slate-900">{c.name}</td>
                  <td className="px-6 py-4 text-slate-600">{c.industry || "—"}</td>
                  <td className="px-6 py-4 text-slate-600">{c.city || "—"}</td>
                  <td className="px-6 py-4 text-slate-600">{c.email || "—"}</td>
                  <td className="px-6 py-4 text-slate-400"><ChevronRight className="w-4 h-4" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Nuovo Contatto</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            {[
              ["name", "Nome azienda *", "col-span-2"],
              ["industry", "Settore", ""],
              ["vat", "P.IVA", ""],
              ["email", "Email", ""],
              ["phone", "Telefono", ""],
              ["website", "Sito web", ""],
              ["city", "Città", ""],
              ["address", "Indirizzo", "col-span-2"],
            ].map(([k, label, cls]) => (
              <div key={k} className={cls}>
                <Label className="font-mono-label text-[10px] text-slate-500">{label}</Label>
                <Input data-testid={`company-${k}-input`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="mt-1.5 rounded-sm" />
              </div>
            ))}
            <div className="col-span-2">
              <Label className="font-mono-label text-[10px] text-slate-500">Note</Label>
              <Textarea data-testid="company-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Annulla</Button>
            <Button data-testid="save-company-btn" onClick={save} className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
